import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import {
  decidesOnProject,
  isProjectScoped,
  mayCreateProject,
  postsHeldBy,
  projectAccessOf,
  relationOf,
  worksOnProject,
  type ProjectAccess,
  type ProjectPosts,
} from '@brickup/shared/constants'
import { authUserQuery } from '@/api/auth'
import { selectUserRoles, useAppStore } from '@/stores/use-app-store'

/** What deciding the question needs of a project: who holds each of its posts,
 *  and who was given access beside them. */
export type Assigned = ProjectPosts & {
  collaboratorUserIds?: readonly number[]
}

type Rule = (project: Assigned | null | undefined) => boolean

/** What a project says about a user, from the ids it carries. */
export const relationToProject = (
  project: Assigned,
  userId: number | null | undefined,
) =>
  relationOf({
    posts: postsHeldBy(project, userId),
    collaborates:
      userId != null &&
      (project.collaboratorUserIds?.includes(userId) ?? false),
  })

/**
 * The reader's access to a project, by the rule `ProjectAccessGuard` applies:
 * `projectAccessOf`, fed what the project says about the reader. Answered per
 * project off one subscription, so a list asks it once per row.
 */
function useProjectAccessRule(): (
  project: Assigned | null | undefined,
) => ProjectAccess | null {
  const roles = useAppStore(selectUserRoles)
  const { data: user } = useQuery(authUserQuery)
  const userId = user?.id

  return useCallback(
    project => {
      if (!project) return null
      return projectAccessOf(roles, relationToProject(project, userId))
    },
    [roles, userId],
  )
}

/**
 * Whether this reader may change a project, rather than only read it.
 *
 * An affordance, not the rule: the server asks `ProjectAccessGuard` the same
 * question about the record the request actually names.
 */
export function useCanChangeProject(project: Assigned | null | undefined) {
  return useProjectAccessRule()(project) !== null
}

/**
 * Whether the reader decides about a project — removing it, who holds its
 * posts, and who else works on it. A predicate, so a list asks it per row.
 */
export function useDecidesOnProjectRule(): Rule {
  const accessOf = useProjectAccessRule()
  return useCallback(project => decidesOnProject(accessOf(project)), [accessOf])
}

/**
 * Whether the reader decides who works on the one project a screen shows —
 * who holds its posts, and who is given access beside them. One question, so
 * the staffing rows and the access list cannot offer different answers.
 */
export const useCanManageCollaborators = (
  project: Assigned | null | undefined,
) => useDecidesOnProjectRule()(project)

/**
 * Whether the reader may write what hangs off this project — its notes, the
 * documents filed on it. Wider than changing the project, and refused by the
 * same guard the server applies.
 */
export function useCanWorkOnProject(project: Assigned | null | undefined) {
  const roles = useAppStore(selectUserRoles)
  return worksOnProject(roles, useProjectAccessRule()(project))
}

/** Whether the reader may start a project. */
export function useCanCreateProject(): boolean {
  return mayCreateProject(useAppStore(selectUserRoles))
}

/**
 * Whether the reader holds a role a project is scoped to the people it names,
 * and so opens their list on their own projects.
 *
 * Narrower than `ADVISOR_ROLES`, which is every role that advises a customer:
 * an admin advises too and is scoped to nothing. Wider than the advisor,
 * which is why the name says neither — a project manager and a site manager
 * are scoped the same way.
 */
export function useIsProjectScoped(): boolean {
  return isProjectScoped(useAppStore(selectUserRoles))
}

/**
 * The signed-in user's id, or `null` while the account is still loading.
 *
 * What a screen needs to recognise the reader among other people — their own
 * row in a roster of advisors — whatever their role scopes them to.
 */
export function useViewerId(): number | null {
  const { data: user } = useQuery(authUserQuery)
  return user?.id ?? null
}
